<template>
  <div class="flex flex-col">
    <div class="max-w-736 w-full mx-auto my-24 px-16 sm:px-0">
      <Breadcrumb href="/account/settings" header="Account" :anchorLink="true" />
      <Card class="mt-24 sm:p-24">
        <h1 class="text-h3 sm:text-h2 mb-8">Logged-in devices</h1>
        <p class="text-body text-grey-text-subdued mb-24">
          These are the browsers and apps where you are signed in to Sittercity.
        </p>

        <GenericError
          v-if="loadError"
          title="Unable to load devices"
          buttonValue="Back to Account"
          buttonHref="/account/settings"
        />

        <EmptyStateCard
          v-else-if="!isLoading && sessions.length === 0"
          title="No devices found"
          message="We do not have any signed-in devices on file for your account yet."
          actionText="Back to Account"
          actionUrl="/account/settings"
        />

        <div v-else>
          <div v-if="isLoading" class="text-body text-grey-text-subdued">Loading devices...</div>
          <FeatureTable v-else :tableHeadings="tableHeadings">
            <FeatureTableRow v-for="(session, index) in sessions" :key="`${session.device_label}-${index}`" :index="index">
              <td class="py-12 pr-16 text-left text-body">{{ session.device_label }}</td>
              <td class="py-12 px-16 text-center text-body">{{ formatTimestamp(session.last_seen_at) }}</td>
              <td class="py-12 pl-16 text-center text-body">{{ deviceSessionStatus(session) }}</td>
            </FeatureTableRow>
          </FeatureTable>
        </div>

        <p v-if="!loadError && !isLoading && sessions.length > 0" class="text-small text-grey-text-subdued mt-24 mb-0">
          If you do not recognize a device, change your password on the
          <a class="underline" href="/account/settings">Account</a>
          page. That will sign you out everywhere.
        </p>
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import { format, parseISO } from 'date-fns';
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import Card from '@/components/card/Card.vue';
import FeatureTable from '@/components/feature_table/FeatureTable.vue';
import FeatureTableRow from '@/components/feature_table/FeatureTableRow.vue';
import GenericError from '@/components/generic_error/GenericError.vue';
import EmptyStateCard from '@/components/empty_state_card/EmptyStateCard.vue';
import { DeviceSession, deviceSessionStatus, fetchDeviceSessions } from '@/lib/deviceSessions';

export default {
  name: 'DeviceSessions',
  components: {
    Breadcrumb,
    Card,
    FeatureTable,
    FeatureTableRow,
    GenericError,
    EmptyStateCard,
  },
  data() {
    return {
      sessions: [] as DeviceSession[],
      isLoading: true,
      loadError: false,
      tableHeadings: ['Device', 'Last seen', 'Status'],
    };
  },
  async mounted() {
    const response = await fetchDeviceSessions();

    this.isLoading = false;

    if (!response) {
      this.loadError = true;
      return;
    }

    this.sessions = response.device_sessions;
  },
  methods: {
    deviceSessionStatus,
    formatTimestamp(value: string | null): string {
      if (!value) return '—';

      try {
        return format(parseISO(value), 'MMM d, yyyy h:mm aa');
      } catch (_error) {
        return value;
      }
    },
  },
};
</script>
